import { useState, useEffect, useRef, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { generateEventMediaAPI, uploadEventMediaAPI } from '@/api/room'
import { EventMediaItem } from '@/redux/types/room'
import { toast } from 'sonner'
import {
  SUPPORTED_IMAGE_FORMATS_LABEL,
  MAX_EVENT_IMAGE_SIZE_LABEL,
  MAX_EVENT_MEDIA_PER_EVENT,
  validateEventImageFile,
} from '@/constants/eventMedia'
import { Upload, ImageIcon } from 'lucide-react'

const MAX_EVENT_TEXT_LENGTH = 500

function buildInitialPrompt(
  scenarioTitle: string,
  eventTitle: string,
  eventText: string
): string {
  const truncated = (eventText || '').trim().slice(0, MAX_EVENT_TEXT_LENGTH)
  const parts: string[] = []
  if (scenarioTitle) parts.push(`Scenario: ${scenarioTitle}.`)
  if (eventTitle) parts.push(`Event: ${eventTitle}.`)
  if (truncated) parts.push(`Description: ${truncated}.`)
  parts.push('Generate an image that illustrates this event for worldbuilding.')
  return parts.join(' ').trim()
}

const ACCEPT_MIME = 'image/jpeg,image/png,image/webp,image/svg+xml'

export interface EventMediaGeneratorChatProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  roomId: string
  eventId: number
  scenarioTitle: string
  eventTitle: string
  eventText: string
  existingMedia?: EventMediaItem[]
  onAccept: (media: EventMediaItem) => void
}

export function EventMediaGeneratorChat({
  open,
  onOpenChange,
  roomId,
  eventId,
  scenarioTitle,
  eventTitle,
  eventText,
  existingMedia = [],
  onAccept,
}: EventMediaGeneratorChatProps) {
  const atMediaLimit = existingMedia.length >= MAX_EVENT_MEDIA_PER_EVENT
  const [prompt, setPrompt] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [generatedResult, setGeneratedResult] = useState<EventMediaItem | null>(
    null
  )

  const [generatedImageB64, setGeneratedImageB64] = useState<string | null>(null)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadPreview, setUploadPreview] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadLoading, setUploadLoading] = useState<boolean>(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const initialPrompt = buildInitialPrompt(scenarioTitle, eventTitle, eventText)

  useEffect(() => {
    if (open) {
      setPrompt(initialPrompt)
      setError(null)
      setGeneratedResult(null)
      setGeneratedImageB64(null)
      setUploadFile(null)
      setUploadPreview(null)
      setUploadError(null)
    }
  }, [open, initialPrompt])

  useEffect(() => {
    if (uploadFile) {
      const url = URL.createObjectURL(uploadFile)
      setUploadPreview(url)
      return () => URL.revokeObjectURL(url)
    }
    setUploadPreview(null)
  }, [uploadFile])

  const handleGenerate = async () => {
    if (atMediaLimit) {
      toast.error(`Maximum ${MAX_EVENT_MEDIA_PER_EVENT} images per event. Remove some to add more.`)
      return
    }
    if (!prompt.trim()) {
      toast.error('Please enter a prompt')
      return
    }
    setLoading(true)
    setError(null)
    setGeneratedResult(null)
    setGeneratedImageB64(null)
    try {
      const result = await generateEventMediaAPI(roomId, {
        event_id: eventId,
        prompt: prompt.trim(),
        media_type: 'image',
        scenario_title: scenarioTitle,
        event_title: eventTitle,
        event_text: eventText,
      })
      if (!result.image_b64) {
        throw new Error('No image returned from generator')
      }
      setGeneratedImageB64(result.image_b64)
      setGeneratedResult({
        id: 0,
        type: result.media_type ?? 'image',
        url: `data:image/png;base64,${result.image_b64}`,
      })
      toast.success('Image generated')
    } catch (err) {
      const rawMessage =
        err instanceof Error ? err.message : 'Generation failed'
      const isEventNotFound =
        /not found|404/i.test(rawMessage) ||
        rawMessage.toLowerCase().includes('event not found')
      const message = isEventNotFound
        ? 'Save the scenario first to add media to this event.'
        : rawMessage
      setError(message)
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  const handleAddGeneratedToEvent = () => {
    if (atMediaLimit) return
    if (!generatedResult || !generatedImageB64) return
    ;(async () => {
      try {
        const byteCharacters = atob(generatedImageB64)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i += 1) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: 'image/png' })
        const file = new File([blob], `generated-${Date.now()}.png`, {
          type: 'image/png',
        })

        const result = await uploadEventMediaAPI(roomId, eventId, file)
        onAccept({ id: result.id, type: result.type, url: result.url })
        setGeneratedResult(null)
        setGeneratedImageB64(null)
        onOpenChange(false)
        toast.success('Media added to event')
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to add media to event'
        toast.error(message)
      }
    })()
  }

  const handleDiscard = () => {
    setGeneratedResult(null)
    setGeneratedImageB64(null)
  }

  const processFile = useCallback((file: File | null) => {
    setUploadError(null)
    setUploadFile(null)
    if (!file) return
    const result = validateEventImageFile(file)
    if (!result.valid) {
      setUploadError(result.error ?? 'Invalid file')
      return
    }
    setUploadFile(file)
  }, [])

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    processFile(file ?? null)
    e.target.value = ''
  }

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const file = e.dataTransfer.files?.[0]
      processFile(file ?? null)
    },
    [processFile]
  )

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleUploadAddToEvent = async () => {
    if (atMediaLimit) {
      toast.error(`Maximum ${MAX_EVENT_MEDIA_PER_EVENT} images per event. Remove some to add more.`)
      return
    }
    if (!uploadFile) return
    setUploadLoading(true)
    setUploadError(null)
    try {
      const result = await uploadEventMediaAPI(roomId, eventId, uploadFile)
      onAccept({ id: result.id, type: result.type, url: result.url })
      setUploadFile(null)
      onOpenChange(false)
      toast.success('Media added to event')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed'
      setUploadError(message)
      toast.error(message)
    } finally {
      setUploadLoading(false)
    }
  }

  const handleUploadClear = () => {
    setUploadFile(null)
    setUploadError(null)
  }

  const handleBrowseClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <DialogTitle>Add event media</DialogTitle>
          <DialogDescription>
            {atMediaLimit
              ? `This event already has the maximum of ${MAX_EVENT_MEDIA_PER_EVENT} images. Remove some to add more.`
              : 'Generate an image or upload an image file for this event.'}
          </DialogDescription>
        </DialogHeader>
        {atMediaLimit ? (
          <p className='text-sm text-muted-foreground py-2' role='status'>
            Maximum {MAX_EVENT_MEDIA_PER_EVENT} images per event. Remove an image from this event to add another.
          </p>
        ) : (
        <Tabs defaultValue='generate' className='w-full'>
          <TabsList className='grid w-full grid-cols-2' aria-label='Generate or upload media'>
            <TabsTrigger value='generate'>
              <ImageIcon className='w-4 h-4 mr-1.5' />
              Generate
            </TabsTrigger>
            <TabsTrigger value='upload'>
              <Upload className='w-4 h-4 mr-1.5' />
              Upload
            </TabsTrigger>
          </TabsList>
          <TabsContent value='generate' className='space-y-4 mt-4'>
            <div>
              <label className='block text-sm font-medium mb-1'>Prompt</label>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder='Describe the image to generate...'
                rows={4}
                className='resize-none'
                disabled={loading}
              />
            </div>
            {error && (
              <p className='text-sm text-red-600' role='alert'>
                {error}
              </p>
            )}
            <div className='flex gap-2'>
              <Button
                type='button'
                onClick={handleGenerate}
                disabled={loading}
                className='bg-dark-blue hover:bg-dark-blue-hovered'
              >
                {loading ? 'Generating...' : 'Generate image'}
              </Button>
            </div>
            {generatedResult && (
              <div className='space-y-3 border rounded-lg p-3 bg-muted/30'>
                <p className='text-sm font-medium'>Preview</p>
                <img
                  src={generatedResult.url}
                  alt='Generated event media'
                  className='max-h-64 w-full object-contain rounded border bg-background'
                />
                <div className='flex gap-2'>
                  <Button
                    type='button'
                    onClick={handleAddGeneratedToEvent}
                    className='bg-green-600 hover:bg-green-700'
                  >
                    Add to event
                  </Button>
                  <Button type='button' variant='outline' onClick={handleDiscard}>
                    Discard
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
          <TabsContent value='upload' className='mt-4 space-y-4'>
            <div
              role='button'
              tabIndex={0}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={handleBrowseClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleBrowseClick()
                }
              }}
              className='border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
              aria-label='Drop image here or click to browse'
            >
              <input
                ref={fileInputRef}
                type='file'
                accept={ACCEPT_MIME}
                onChange={handleFileInputChange}
                className='sr-only'
                aria-hidden
              />
              <Upload className='w-10 h-10 mx-auto text-muted-foreground mb-2' />
              <p className='text-sm font-medium'>Drop image here or browse</p>
              <p className='text-xs text-muted-foreground mt-1'>
                {SUPPORTED_IMAGE_FORMATS_LABEL} · {MAX_EVENT_IMAGE_SIZE_LABEL}
              </p>
            </div>
            {uploadError && (
              <p className='text-sm text-red-600' role='alert'>
                {uploadError}
              </p>
            )}
            {uploadPreview && uploadFile && (
              <div className='space-y-3 border rounded-lg p-3 bg-muted/30'>
                <p className='text-sm font-medium'>Preview</p>
                <img
                  src={uploadPreview}
                  alt='Upload preview'
                  className='max-h-64 w-full object-contain rounded border bg-background'
                />
                <div className='flex gap-2'>
                  <Button
                    type='button'
                    onClick={handleUploadAddToEvent}
                    disabled={uploadLoading}
                    className='bg-green-600 hover:bg-green-700'
                  >
                    {uploadLoading ? 'Uploading…' : 'Add to event'}
                  </Button>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={handleUploadClear}
                    disabled={uploadLoading}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
        )}
      </DialogContent>
    </Dialog>
  )
}
